import React, { useSyncExternalStore } from 'react';
import { mobileBreakpoint } from '../entrypoints/theme.jsx';
import { lazyComponents } from '@src/utilities/LazyComponent';
import { SearchSettingsProvider } from '@src/contexts/SearchSettingsContext';
import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';
import { SearchHeaderProvider } from '@src/contexts/SearchHeaderContext';
import { withBlockEnhancements, useInputEvents } from '../src/utils/blockEnhancements.jsx';

// Search Block Components
export const SearchComponent = withBlockEnhancements(
  ({ settings }) => {
    const SearchHeader = lazyComponents['SearchHeader'];

    // Import search provider dynamically
    const [SearchProviderComponent, setSearchProviderComponent] = React.useState(null);

    React.useEffect(() => {
      // Dynamically import the search provider
      import('@src/providers/Search')
        .then((module) => {
          const searchSystem = typeof window !== 'undefined' && window.shopifySettings ? 'shopify' : 'shopify';
          const Provider = module.getSearchProvider(searchSystem);
          setSearchProviderComponent(() => Provider);
        })
        .catch((error) => {
          console.warn('SearchComponent: Failed to load search provider:', error);
        });
    }, []);

    // If search provider is not loaded yet or failed to load, render without it
    if (!SearchProviderComponent) {
      return <SearchHeader settings={settings} />;
    }

    // Wrap SearchHeader with the search provider
    return (
      <SearchProviderComponent
        settings={typeof window !== 'undefined' && window.shopifySettings ? window.shopifySettings : {}}
        searchType='header-search'
      >
        <SearchHeader settings={settings} />
      </SearchProviderComponent>
    );
  },
  { enableEvents: true, enableAnimations: true, enablePositioning: true }
);
SearchComponent.displayName = 'theme__block-search';

export const SearchSuggestionsComponent = withBlockEnhancements(
  ({ settings }) => {
    const SearchSuggestions = lazyComponents['SearchSuggestions'];
    const blockSettings = settings.block_settings || {};
    const title = blockSettings.title || 'Search suggestions';
    const suggestionsList =
      blockSettings.suggestions_list ||
      'Luggage, Carry-On Luggage, Check-In Medium Luggage, Check-In Large Luggage, Soft Luggage, Hard Luggage, Luggage Sets';
    const suggestions = suggestionsList
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    return (
      <SearchSuggestions
        title={title}
        suggestions={suggestions}
        settings={blockSettings}
      />
    );
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
SearchSuggestionsComponent.displayName = 'theme__block-search-suggestions';

export const SearchCategoriesComponent = withBlockEnhancements(
  ({ settings }) => {
    const SearchCategories = lazyComponents['SearchCategories'];
    const blockSettings = settings.block_settings || {};

    const title = blockSettings.title || 'Category';
    const categoriesList = blockSettings.categories_list || 'Luggage, Travel Bags, Accessories';
    const categories = categoriesList
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((name) => ({ name }));
    const popularCollections = {
      collections: blockSettings.collections_list,
      title: blockSettings.collections_title,
      showWhen: blockSettings.collections_show_when
    };
    const featuredCollection = {
      label: blockSettings.featured_collection_cta_label,
      link: blockSettings.featured_collection_link,
      image: blockSettings.featured_collection_image,
      showWhen: blockSettings.featured_collection_show_when
    };

    return (
      <SearchHeaderProvider>
      <SearchCategories
        title={title}
        categories={categories}
        popularCollections={popularCollections}
        featuredCollection={featuredCollection}
        settings={blockSettings}
      />
      </SearchHeaderProvider>
    );
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
SearchCategoriesComponent.displayName = 'theme__block-search-categories';


SearchInputComponent.displayName = 'theme__block-search-input';
export function SearchInputComponent({ ...props }) {
  const SearchInputBlock = lazyComponents['SearchInputBlock'];
  return (
    <SearchSettingsProvider>
      <SearchInputBlock settings={props} />
    </SearchSettingsProvider>
  );
}

SearchResultsBlockComponent.displayName = 'theme__block-search-results';
export function SearchResultsBlockComponent({ ...props }) {
  const SearchResultsBlock = lazyComponents['SearchResultsBlock'];
  console.log('Search Results', props);
  return (
    <SearchSettingsProvider>
      <SearchResultsBlock settings={props} />
    </SearchSettingsProvider>
  );
}

PopularProductsComponent.displayName = 'theme__block-popular-products';
export function PopularProductsComponent({ ...props }) {
  const PopularProductsBlock = lazyComponents['PopularProductsBlock'];
  console.log('Popular Products', props);
  
  return (
    <SearchHeaderProvider>
      <SearchSettingsProvider>
        <PopularProductsBlock settings={props} />
      </SearchSettingsProvider>
    </SearchHeaderProvider>
  );
}

PopularSearchesComponent.displayName = 'theme__block-popular-searches';
export function PopularSearchesComponent({ ...props }) {
  const PopularSearchesBlock = lazyComponents['PopularSearchesBlock'];
  return (
    <SearchSettingsProvider>
      <PopularSearchesBlock settings={props} />
    </SearchSettingsProvider>
  );
}


export const SearchFiltersComponent = withBlockEnhancements(
  ({ settings }) => {
    const SearchFilters = lazyComponents['SearchFilters'];
    const blockSettings = settings.block_settings || {};
    const { filters_list } = blockSettings;

    return (
      <SearchFilters
        enabledFilters={filters_list?.split(',')?.map((filter) => filter.trim())}
        settings={blockSettings}
      />
    );
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
SearchFiltersComponent.displayName = 'theme__block-search-filters';

export const SearchColorsComponent = withBlockEnhancements(
  ({ settings }) => {
    const SearchColors = lazyComponents['SearchColors'];
    const blockSettings = settings.block_settings || {};

    const title = blockSettings.title || 'Colours';
    const colorsList =
      blockSettings.colors_list ||
      'Beige:beige:bg-orange-100, Sage:sage:bg-green-200, Navy:navy:bg-blue-900, Burgundy:burgundy:bg-red-900, Lavender:lavender:bg-purple-300, Dusty Blue:dusty-blue:bg-blue-300, Forest Green:forest-green:bg-green-800, Pink:pink:bg-pink-300, Turquoise:turquoise:bg-teal-400';

    const colors = colorsList
      .split(',')
      .map((colorStr) => {
        const parts = colorStr.trim().split(':');
        return {
          name: parts[0] || '',
          value: parts[1] || '',
          colorClass: parts[2] || 'bg-gray-200'
        };
      })
      .filter((color) => color.name.length > 0);

    return (
      <SearchColors
        title={title}
        colors={colors}
      />
    );
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
SearchColorsComponent.displayName = 'theme__block-search-colors';


MinicartComponent.displayName = 'theme__ecommerce-minicart';
export function MinicartComponent({ ...props }) {
  const Minicart = lazyComponents['Minicart'];

  return <Minicart settings={props.settings} />;
}

CollectionPageComponent.displayName = 'theme__collection-main';
export function CollectionPageComponent({ ...props }) {
  const Collection = lazyComponents['Collection'];

  return (
    <SearchSettingsProvider>
      <Collection
        settings={props.settings}
        collectionProducts={props.settings?.collection_products}
      />
    </SearchSettingsProvider>
  );
}

SearchResultsPageComponent.displayName = 'theme__search-main';
export function SearchResultsPageComponent({ ...props }) {
  const SearchResults = lazyComponents['SearchResults'];
  return (
    <SearchSettingsProvider>
      <SearchResults settings={props.settings} />
    </SearchSettingsProvider>
  );
}

HeaderSearchResultsComponent.displayName = 'theme__search-header-results';
export function HeaderSearchResultsComponent({ ...props }) {
  const HeaderSearchResults = lazyComponents['HeaderSearchResults'];
  return (
    <SearchSettingsProvider>
      <HeaderSearchResults settings={props.settings} />
    </SearchSettingsProvider>
  );
}

CartItemsComponent.displayName = 'theme__cart-items';
export function CartItemsComponent({ ...props }) {
  const CartItems = lazyComponents['CartItems'];
  return <CartItems settings={props.settings} />;
}

CartSummaryComponent.displayName = 'theme__cart-summary';
export function CartSummaryComponent({ ...props }) {
  const CartSummary = lazyComponents['CartSummary'];
  return <CartSummary settings={props.settings} />;
}

CartDiscountsComponent.displayName = 'theme__cart-discounts';
export function CartDiscountsComponent({ ...props }) {
  const CartDiscounts = lazyComponents['MinicartDiscounts'];
  const { cartData } = useShoppingBasketCart();
  if (
    cartData?.items &&
    cartData.items.length > 0 &&
    cartData.items_subtotal_price &&
    cartData.items_subtotal_price > 0
  ) {
    return (
      <CartDiscounts
        cart_subtotal={cartData.items_subtotal_price / 100}
        settings={props.settings.block_settings}
      />
    );
  }
}

AccountForgotPasswordComponent.displayName = 'theme__account-forgotpassword';
export function AccountForgotPasswordComponent({ ...props }) {
  const AccountForgotPassword = lazyComponents['AccountForgotPassword'];
  return <AccountForgotPassword settings={props.settings} />;
}
